// TODO: Add other types since this is added now

import { IAppointment } from '../../API/GoMoto';
import { GoMotoFunction } from '../../API/GoMoto.types';
import { Locales } from '../Localization/localization.types';
import { ILockerFixture } from '../lockers';

// #region useAppointmentHistory
/**
 * API call parameters for the `InsertLastPageVisited` endpoint.
 */
export interface InsertLastPageVisitedParams {
    /**
     * A string of the specific route visited.
     */
    lastPageVisited: string;
    /**
     * The key of the associated mobile check-in appointment that we are
     * recording the history of.
     */
    appointmentKey: IAppointment['key'];
}

/**
 * The return object for the `useAppointmentHistory` hook.
 */
export interface AppointmentHistoryReturn {
    /**
     * The API function that records the page visited by a specific appointment
     * by calling the `InsertLastPageVisited` endpoint.
     */
    insertLastPageVisited: GoMotoFunction<[string, IAppointment['key']?], void>;
}

/**
 * The hook type definition for the `useAppointmentHistory` hook.
 */
export type AppointmentHistoryHook = GoMotoFunction<void, AppointmentHistoryReturn>;
// #endregion

/*
 * #region useFormatDateTime
 * #region useUpdateAppointment
 */
/**
 * The props object for the `updateSurveyQuestions` API call function. The function props allow
 * for any of the survey questions results to be passed to it.
 */
export type UpdateSurveyQuestionsProps = Pick<IAppointment, 'customerWouldRepeat' | 'customerDidEnjoy'>;

/**
 * Return object for the `useUpdateAppointment` hook with all of the API call functions
 * that update our database with parts of the appointment that were updated on the Kiosk.
 */
export interface UpdateAppointmentReturn {
    /**
     * API call function that updates the appointment object in our database with the results
     * from any of the survey questions.
     */
    updateSurveyQuestions: GoMotoFunction<UpdateSurveyQuestionsProps, void>;
}

/**
 * The hook type definition for the `useUpdateAppointment` hook.
 */
export type UpdateAppointmentHook = GoMotoFunction<void, UpdateAppointmentReturn>;
// #endregion

// #region useFormatTime
/**
 * String enum of each of the default time formats that are used when we take a Date or string and
 * format it into the correct time format for display.
 */
export enum AppointmentTimeFormatTypes {
    /**
     * The default time formatting used when displaying a Date or string Date and Time.
     */
    Default = 'Default',
    /**
     * The time formatting used for a check-in that is completed.
     */
    CheckInComplete = 'CheckInComplete',
    /**
     * The time formatting used by the appointment cards in the Key Management screen.
     */
    AppointmentCard = 'AppointmentCard'
}

/**
 * Defines the object type that requires each locale except `none` to be a key and a string
 * to be defined as the associated value that represents a time format for that specific locale.
 */
export type AppointmentTimeFormats = Readonly<Record<Exclude<Locales, Locales.none>, string>>;

/**
 * Function props object for the formatDateTimeWIthTimeFormatType. This is used when we don't need
 * to define a custom time format and can use the ones that we already have pre-defined.
 *
 * @note This exists primarily to replace how we already have a few places that use custom time formats.
 * Realistically, all time formats should be added to this hook and not passed through this function if possible.
 */
export interface FormatDateTimeWithTimeFormatType {
    /**
     * The Date or string we will be formatting. If undefined, we will instead format the `appointment.appointmentDateTime` value.
     */
    appointmentDateTime?: Date | string;
    /**
     * The specific type of time formatting to use from the default object of time formats by locale.
     */
    timeFormatType: AppointmentTimeFormatTypes;
}

export interface FormatDateTimeWithTimeFormats {
    /**
     * The Date or string we will be formatting. If undefined, we will instead format the `appointment.appointmentDateTime` value.
     */
    appointmentDateTime?: Date | string;
    /**
     * The custom time format object to be used to format with by locale.
     */
    timeFormats: AppointmentTimeFormats;
}

/**
 * The return object for the `useFormatAppointmentDateTime` hook that returns functions
 * to be used to format Dates or strings into the correct display time format.
 */
export interface FormatAppointmentDateTimeReturn {
    /**
     * This function formats a time passed in, or the `appointment.appointmentDateTime` value, to a specific
     * pre-defined time format based on the current locale.
     */
    formatDateTimeWithTimeFormatType: GoMotoFunction<FormatDateTimeWithTimeFormatType, string>;
    /**
     * This function formats a time passed in, or the `appointment.appointmentDateTime` value, to a specific
     * time format that is custom defined and passed in as a prop on this function.
     */
    formatDateTimeWithCustomTimeFormat: GoMotoFunction<FormatDateTimeWithTimeFormats, string>;
}

/**
 * The hook type definition for the `useFormatAppointmentDateTime` hook.
 */
export type FormatAppointmentDateTimeHook = GoMotoFunction<void, FormatAppointmentDateTimeReturn>;
// #endregion

// #region useLockerOperation
/**
 * String enum of each of the three steps for any of the supported
 * locker operations.
 */
export enum LockerOperationSteps {
    /**
     * Step after we start to check if the locker criteria we provided
     * is valid or if a valid locker exists to meet the criteria we have.
     */
    LockerCheck = 'locker-check',
    /**
     * This step is the logic that results in the locker opening.
     */
    LockerOpen = 'locker-open',
    /**
     * This step occurs when the locker is closed and all of the locker-open
     * logic has fully resolved.
     */
    LockerClosed = 'locker-closed'
}

/**
 * Enum of the possible errors that we will log if a problem occurs while
 * performing a locker operation.
 */
export enum KeyDropError {
    /**
     * The default error indicating everything is okay.
     */
    none = 0,
    /**
     * A catch-all error for anything that does not match any other error
     * in the enum, and is not one we would commonly expect.
     */
    unknown,
    /**
     * No locker is available, either because an error in finding the locker
     * occurred or that there was actually no locker available for the locker
     * operation we were attempting.
     */
    noLockerAvailable,
    /**
     * A key is already stored for this appointment.
     */
    keyAlreadyStored,
    /**
     * The locker failed to open for some reason.
     */
    failedToOpen
}

/**
 * Enum that handles all of the stages of the key exchange process to
 * ensure we correctly handle the appointment status after each stage.
 */
export enum KeyExchangeStage {
    /**
     * The customer's key is stored, and the advisor has indicated the
     * customer will be èxchanging a rental or loaner key when picking
     * up their key.
     */
    CustomerPickupKeyExchange = 0,
    /**
     * The customer has exchanged the rental or loaner key for their
     * vehicle key and the rental or loaner key is now ready for the
     * advisor to pick up to complete the appointment.
     */
    AdminKeyRetrieve = 1
}

/**
 * Type definition for the useLockerOperation hook props object.
 */
export interface LockerOperationProps {
    /**
     * Whether the locker operation is being performed by an advisor. By default is falsy.
     */
    byAdvisor?: boolean;
}

/**
 * Union type of all possible locker operations that could occur with a default `'not-started'` value
 * to initialize what kind of locker operation type prior to the actual type being passed in as a prop
 * to the start function.
 */
export type LockerActionType = 'not-started' | 'store-key' | 'retrieve-key' | 'pickup-and-exchange-key' | 'retrieve-exchanged-key' | 'open-locker';

/**
 * Type definition for the Start function props object. This object is how the useLockerOperation hook
 * initialized most of its' states before beginning the locker operation.
 */
export interface StartProps {
    /**
     * What kind of locker operation that will be performed.
     *
     * `'not-initialized'` is excluded since it is only meant to be a default initialized state.
     * If it persists past the start function executing, we will know an error has occurred in
     * setting the state of the useLockerOperation hook.
     */
    lockerAction: Exclude<LockerActionType, 'not-initialized'>;
    /**
     * The locker ID of the locker we will be performing a locker operation on. This is mainly
     * used for `'open-locker'` operations on lockers that have no appointment attached to them.
     */
    lockerId?: ILockerFixture['Id'];
    /**
     * Whether the operation being performed is a manual operation being performed
     * by the advisor using the admin tools.
     */
    manual?: boolean;
    /**
     * Whether we should reset attempts to store a key if the `'store-key'` operation fails.
     */
    resetAttempts?: boolean;
    /**
     * When true, complete the appointment after the locker operation.
     */
    completeAppointment?: boolean;
    /**
     * The appointment associated with the locker operation. This is only used with the 24/7 to
     * handle unique issues with key retrieval.
     */
    appt?: IAppointment | undefined;
}

/**
 * Return object definition for the useLockerOperation hook.
 */
export interface LockerOperationReturn {
    /**
     * Callback function to set most of the initial state values using the props of this function
     * and begin the locker operation indicated in the props.
     */
    start: GoMotoFunction<StartProps, Promise<void>>;
    /**
     * When true, the locker operation is currently in progress.
     */
    inProgress: boolean;
    /**
     * When true, the locker operation is completed.
     */
    complete: boolean;
    /**
     * When true, the locker operation has proceeded past open.
     */
    hasOpened: boolean;
    /**
     * The locker that the locker operation is being performed on. This can be undefined
     * even after we check the lockers, but this will result in an error.
     */
    locker?: ILockerFixture;
    /**
     * By default this is set to `KeyDropError.none`, but if it changes to any other value
     * then an error has occurred during the locker operation and this is used to then display
     * a localized error to the user.
     */
    error: KeyDropError;
    /**
     * Forces the locker to advance to the closed/completed state even if it is not physically detected
     * to be so.
     */
    forceLockerClose: GoMotoFunction<void, Promise<void>>;
}

/**
 * The type definition for the useLockerOperation hook.
 *
 * @prop For details about the props object, click {@link LockerOperationProps here}.
 * @returns For details about the return object, click {@link LockerOperationReturn here}.
 */
export type LockerOperationHook = GoMotoFunction<[LockerOperationProps?], LockerOperationReturn>;

/**
 * The type definition for the internal LockerActionSettings state object. This object
 * is used to aggregate most of the data that is needed to perform or while performing the
 * locker operation.
 *
 * This is the base object that is extended by other interfaces before creating a union type
 * of the possible objects. This ensures that when certain locker operations are performed,
 * specific props we need or don't need are excluded or included.
 */
export interface LockerActionSettingsBase {
    /**
     * What locker is being used to perform the current locker operation.
     */
    locker?: ILockerFixture;
    /**
     * What error, if any, has occurred during this locker operation.
     */
    error: KeyDropError;
    /**
     * If this locker operation is being performed manually by an advisor.
     */
    manual: boolean;
}

/**
 * The LockerActionSettings object for the `'store-key'` locker operation.
 */
export interface StoreKeyLockerActionSettings extends LockerActionSettingsBase {
    /**
     * This value can only be `'store-key'` and is used to enforce that `resetAttempts` can only
     * be in the object if the locker action is set to `'store-key'`.
     */
    lockerAction: Exclude<LockerActionType, 'not-started' | 'retrieve-key' | 'pickup-and-exchange-key' | 'retrieve-exchanged-key' | 'open-locker'>;
    /**
     * Whether we will reset attempts if a `'store-key'` locker operation fails.
     */
    resetAttempts: boolean;
}

/**
 * The LockerActionSettings object for any retrieve or exchange locker operation, and also for the `'open-locker'` locker operation.
 */
export interface RetrieveOrExchangeKeyLockerActionSettings extends LockerActionSettingsBase {
    /**
     * This value can be any of the possible values on {@link LockerActionType} except for `'not-started'` and `'store-key'`. This
     * is to ensure that `resetAttempts` can never exist on any of those objects while those locker operations are being performed.
     */
    lockerAction: Exclude<LockerActionType, 'not-started' | 'store-key'>;
    /**
     * Optional never type so that we do not have to specifically set `resetAttempts` to a value, but it also cannot hold any value.
     */
    resetAttempts?: never;
}

/**
 * The default LockerActionSettings object that is initialized before any locker operation is performed and reset to after
 * any locker operation completes.
 */
export interface NotStartedLockerActionSettings extends Omit<LockerActionSettingsBase, 'locker'> {
    /**
     * This value can only be `'not-started'` so that if for some reason it persists past the start function beginning the
     * locker operation, we can throw an error.
     */
    lockerAction: Exclude<LockerActionType, 'store-key' | 'retrieve-key' | 'pickup-and-exchange-key' | 'retrieve-exchanged-key' | 'open-locker'>;
    /**
     * Redefines the `locker` type to exclusively be undefined so that it is not possible to reset or initialize this object
     * with a locker object. This ensures that if a locker object is not found or found, we know it was due to the the locker
     * check logic.
     */
    locker: undefined;
    /**
     * Optional never type so that we do not have to specifically set `resetAttempts` to a value, but it also cannot hold any value.
     */
    resetAttempts?: never;
}

/**
 * Union type of the various LockerActionSettings objects to dynamically create objects with different props based on what `lockerAction` is
 * set to.
 */
export type LockerActionSettings = NotStartedLockerActionSettings | StoreKeyLockerActionSettings | RetrieveOrExchangeKeyLockerActionSettings;

/**
 * Object type to indicate that the data that should be used for this locker operation is an appointment
 * object.
 */
export interface LockerOperationWithAppointment {
    /**
     * Indicates that the type of data will be appointment object data.
     */
    type: 'appointment';
    /**
     * Stores the current appointment object for use during the locker operation.
     */
    appointment: IAppointment;
}

/**
 * Object type to indicate that the data that should be used for this locker operation is a specific
 * locker ID. This is used for an `'open-locker'` locker operation being performed on an empty locker.
 */
export interface LockerOperationWithLockerId {
    /**
     * Indicates that the type of data will be a specific locker ID.
     */
    type: 'lockerId';
    /**
     * Stores the number of the specific locker ID that will be used.
     */
    lockerId: ILockerFixture['Id'];
}

/**
 * The union type for whether the object's data will be specific to an appointment object or a locker ID.
 */
export type AppointmentOrLockerId = LockerOperationWithLockerId | LockerOperationWithAppointment;
// #endregion
